import React, { useCallback, useEffect, useState } from "react";

// PROVE 모델 서버(2026-09-29) — 학습한 품목분류 모델을 돌리는 GPU 포드를 관리자가 버튼으로 켜고 끈다.
// 켜기: 조건에 맞는 GPU 를 골라 LoginHS 전용 vast 템플릿으로 포드를 만든다 → 포드가 B2 에서 모델을 받고 vLLM 을 띄운 뒤
// 스스로 주소를 등록한다(5~8분). 끄기: 포드를 삭제해 요금이 멈춘다. 꺼져 있으면 검색 화면의 'PROVE 모델' 줄만 빠진다.
function authHeaders() {
  const token = localStorage.getItem("admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const MUTED = "var(--audit-muted, #6b7482)";
const LINE = "var(--audit-line, #e3e6ea)";
const BLUE = "#1a56db";

function ago(sec) {
  if (sec == null) return "";
  if (sec < 60) return `${sec}초 전`;
  if (sec < 3600) return `${Math.round(sec / 60)}분 전`;
  return `${Math.round(sec / 3600)}시간 전`;
}

export default function AdminHsModel({ API_BASE_URL = "", fetchImpl = fetch, pollMs = 15000 }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-model/status`, { headers: authHeaders() });
      if (r.status === 401 || r.status === 403) { setError("관리자 로그인이 필요합니다(토큰 만료일 수 있습니다)"); return; }
      const j = await r.json();
      setStatus(j); setError(null);
    } catch (e) {
      setError(String(e?.message || e));
    }
  }, [API_BASE_URL, fetchImpl]);

  useEffect(() => {
    load();
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  const act = async (kind) => {
    if (kind === "stop" && !window.confirm("PROVE 모델 서버를 끄시겠습니까? 포드가 삭제되고 요금이 멈춥니다. 검색 화면의 'PROVE 모델' 줄은 빠집니다.")) return;
    setBusy(kind); setMessage(null);
    try {
      const r = await fetchImpl(`${API_BASE_URL}/api/onto/admin/hs-model/${kind}`, { method: "POST", headers: authHeaders() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) setMessage(j.error || `오류 ${r.status}`);
      else if (kind === "start") setMessage(j.result === "already_running" ? "이미 켜져 있습니다." : `켜는 중입니다 — ${j.offer?.gpu || ""} · ${j.offer?.location || ""} · 시간당 ${j.offer?.price_per_hour ?? "?"}달러. 모델 준비까지 5~8분 걸립니다.`);
      else setMessage(`껐습니다 — 삭제한 포드 ${(j.destroyed || []).length}개.`);
      await load();
    } catch (e) {
      setMessage(String(e?.message || e));
    } finally {
      setBusy(null);
    }
  };

  const pods = status?.pods || [];
  const reg = status?.registered;
  const state = !status ? "…" : status.healthy ? "켜짐 · 응답함" : pods.length ? "켜는 중(모델 준비 중)" : "꺼짐";
  return (
    <div data-testid="admin-hs-model" style={{ maxWidth: 820 }}>
      <h3 style={{ margin: "4px 0 6px" }}>PROVE 모델 서버</h3>
      <p style={{ margin: "0 0 14px", color: MUTED, fontSize: 13, lineHeight: 1.6 }}>
        결정례·관세사 신고이력으로 학습한 품목분류 모델을 돌리는 GPU 서버입니다. 켜 둔 시간만 요금이 나갑니다.
        꺼져 있어도 LoginHS 는 그대로 쓰이고, 검색 화면 출처별 세번의 <b>PROVE 모델</b> 줄만 빠집니다.
      </p>
      {error && <div role="alert" style={{ color: "#b42318", marginBottom: 10 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
        <span data-testid="hs-model-state" style={{ fontWeight: 700, color: status?.healthy ? BLUE : "inherit" }}>{state}</span>
        <button type="button" data-testid="hs-model-start" disabled={!!busy || !status?.vast_configured || pods.length > 0} onClick={() => act("start")}>
          {busy === "start" ? "켜는 중…" : "켜기"}
        </button>
        <button type="button" data-testid="hs-model-stop" disabled={!!busy || !status?.vast_configured || pods.length === 0} onClick={() => act("stop")}>
          {busy === "stop" ? "끄는 중…" : "끄기"}
        </button>
        <button type="button" onClick={load} disabled={!!busy}>새로고침</button>
      </div>
      {message && <div data-testid="hs-model-message" style={{ marginBottom: 12, fontSize: 13 }}>{message}</div>}
      {status && !status.vast_configured && <div style={{ color: MUTED, fontSize: 13, marginBottom: 12 }}>서버에 vast 설정(VAST_API_KEY·HS_MODEL_VAST_TEMPLATE)이 없어 버튼을 쓸 수 없습니다.</div>}
      <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
        <tbody>
          <tr><td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}`, color: MUTED, width: 150 }}>쓰는 주소</td>
            <td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}` }}>{status?.using_url || "없음"} {status?.using_source === "registered" ? "(포드가 등록)" : status?.using_source === "configured" ? "(서버 설정값)" : ""}</td></tr>
          <tr><td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}`, color: MUTED }}>마지막 등록</td>
            <td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}` }}>{reg ? `${ago(reg.age_sec)} · ${reg.gpu || ""} · 모델 ${reg.model || ""}` : "없음"}</td></tr>
          {pods.map((p) => (
            <tr key={p.id}><td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}`, color: MUTED }}>포드 {p.id}</td>
              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${LINE}` }}>{p.status} · {p.gpu} {p.gpu_ram_gb}GB · {p.location} · 시간당 {p.price_per_hour}달러{p.status_msg ? ` · ${String(p.status_msg).slice(0, 60)}` : ""}</td></tr>
          ))}
          <tr><td style={{ padding: "6px 8px", color: MUTED }}>고르는 조건</td>
            <td style={{ padding: "6px 8px", color: MUTED }}>GPU 1장 · 메모리 {status?.min_gpu_ram_gb ?? 80}GB 이상 · 시간당 {status?.max_price_per_hour ?? 2.5}달러 이하 · 아시아 먼저, 그다음 싼 순</td></tr>
        </tbody>
      </table>
      {status?.vast_error && <div style={{ color: MUTED, fontSize: 12, marginTop: 8 }}>vast 조회 오류: {status.vast_error}</div>}
    </div>
  );
}
